import React from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { WorkflowIconBox } from '../../utils/workflowIconConfig';
import { SEPPhase } from '../../../shared/types/SEP';
import { spacingTokens } from '../../theme/spacingTokens';
import { typographyTokens, typography } from '../../theme/typographyTokens';

interface SepTaskHeaderBarProps {
  phaseLabel: string;
  workflowLabel: string;
  sepID: number;
  lastSavedLabel?: string;
  openMyNotes?: () => void;
  sepNotesCount?: number; // Optional prop for SEP notes count
}

const SepTaskHeaderBar = ({
  phaseLabel,
  workflowLabel,
  sepID,
  lastSavedLabel,
  openMyNotes,
  sepNotesCount,
}: SepTaskHeaderBarProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: '100%',
        background: `linear-gradient(90deg, ${theme.palette.prime.blue} 0%, ${theme.palette.primeAdditional.midBlue} 100%)`,
        minHeight: 44,
        px: { xs: 1.5, md: 2.5 },
        py: spacingTokens.sm,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: spacingTokens.md,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <WorkflowIconBox workflowType={workflowLabel} phase={phaseLabel as SEPPhase} />

        <Typography
          sx={{
            color: theme.palette.content.white,
            font: `italic normal ${typographyTokens.fontWeights.semibold} ${typography.caption.fontSize}/${typography.caption.fontSize} Open Sans;`,
            textTransform: 'capitalize',
            whiteSpace: 'pre-line',
            mr: spacingTokens.sm,
          }}
        >
          {/* {`${phaseLabel}\n${statusLabel}`} */}
          {phaseLabel}
        </Typography>

        <Typography
          sx={{
            color: theme.palette.content.white,
            font: `normal normal ${typographyTokens.fontWeights.semibold} ${typography.h5.fontSize}/22px Open Sans;`,
            fontSize: { xs: '18px', md: typography.h5.fontSize },
            lineHeight: { xs: '20px', md: '22px' },
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
        >
          {`${sepID} - ${workflowLabel.toUpperCase()}  `}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto', flexWrap: 'wrap' }}>
        {lastSavedLabel && (
          <Typography
            sx={{
              color: theme.palette.content.white,
              font: 'normal normal normal 20px/22px Open Sans;',
              fontSize: { xs: typography.body2.fontSize, md: typography.body1.fontSize },
            }}
          >
            {`Last Saved: ${lastSavedLabel}`}
          </Typography>
        )}
        <Box sx={{ color: theme.palette.content.white, font: 'normal normal normal 20px/22px Open Sans;', fontSize: { xs: typography.body2.fontSize, md: typography.body1.fontSize } }}>
          &nbsp;|&nbsp;
        </Box>
        <Button
          variant="text"
          onClick={openMyNotes}
          sx={{
            minWidth: 'auto',
            p: 0,
            color: theme.palette.content.white,
            font: `normal normal ${typographyTokens.fontWeights.semibold} ${typography.h5.fontSize}/22px Open Sans;`,
            fontSize: { xs: typography.body2.fontSize, md: typography.body1.fontSize },
            textTransform: 'uppercase',
            textUnderlineOffset: '2px',
            '&:hover': {
              textDecoration: 'underline',
              backgroundColor: 'transparent',
            },
          }}
        >
          {/* <i className="fa-solid fa-sticky-note" style={{ marginRight: 8 }} /> */}
          My SEP Notes {sepNotesCount && sepNotesCount > 0 ? `(${sepNotesCount})` : '(0)'}
        </Button>
      </Box>
    </Box>
  );
};

export default SepTaskHeaderBar;